import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { humanizeToolName, toolLabelKey } from "../../lib/toolLabel";
import { Markdown } from "../../components/chat/Markdown";
import { Spinner } from "../../components/settings/Spinner";
import { FoldedLine } from "../components/FoldedLine";
import { Modal } from "../../components/settings/Modal";
import { ContinueBar } from "../continue/ContinueBar";
import { continuable } from "../continue/pending";
import type { ContinueState } from "../continue/useContinueRun";
import { JOURNAL_LOOSE_STEPS, stepCounts, stoppedAtStep, toBlocks, type JournalGroup, type JournalStep, type JournalStepBlock } from "./api";
import "../work-settings/settings.css";
import "./work-journal.css";

const icon = (children: React.ReactNode) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
/** lucide `CircleCheck`, `RotateCcw` and `CircleDashed`: the same quiet set as the transcript's steps. */
const STATE_ICONS = {
  done: icon(<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>),
  failed: icon(<><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></>),
  unconfirmed: icon(<><path d="M10.1 2.18a9.93 9.93 0 0 1 3.8 0" /><path d="M17.6 3.71a9.95 9.95 0 0 1 2.69 2.7" /><path d="M21.82 10.1a9.93 9.93 0 0 1 0 3.8" /><path d="M20.29 17.6a9.95 9.95 0 0 1-2.7 2.69" /><path d="M13.9 21.82a9.94 9.94 0 0 1-3.8 0" /><path d="M6.4 20.29a9.95 9.95 0 0 1-2.69-2.7" /><path d="M2.18 13.9a9.93 9.93 0 0 1 0-3.8" /><path d="M3.71 6.4a9.95 9.95 0 0 1 2.7-2.69" /></>),
};
/** lucide `MessageSquarePlus`: shown on hover, what a click does. */
const ASK_ICON = icon(<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><path d="M12 7v6M9 10h6" /></>);

/** lucide `Image`: the step returned images; always shown, since it says what the step holds. */
const IMAGE_ICON = icon(<><rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></>);

/** Loads a saved step image; resolves to a Blob. */
export type LoadJournalImage = (path: string, signal: AbortSignal) => Promise<Blob>;
/** What the viewer shows: a step's images under its name. */
interface Viewing { name: string; images: string[] }

/**
 * One call by its label. A click asks the agent about it; the panel never shows the code.
 * A step that returned images has its own button beside it: a button cannot hold another.
 */
function Step({ step, onAsk, onView }: {
  step: JournalStep;
  onAsk: ((step: JournalStep, name: string) => void) | null;
  onView: (viewing: Viewing) => void;
}) {
  const { t } = useLocale();
  const name = stepName(step, t);
  const images = step.images ?? [];
  const view = images.length > 1 ? t("workJournal.viewImages", { count: images.length }) : t("workJournal.viewImage");
  const content = <>
    <span className="journal-step-icon">{step.state === "running" ? <Spinner size={14} /> : STATE_ICONS[step.state]}</span>
    <span className="journal-step-name">{name}</span>
    {step.state === "failed" || step.state === "unconfirmed" ? <span className="journal-step-note">{t(`workJournal.${step.state}`)}</span> : null}
    {onAsk ? <span className="journal-step-ask">{ASK_ICON}</span> : null}
  </>;
  return (
    <li className="journal-step-row">
      {/* Before asking is possible the step is plain text, not a disabled button: it reads as before, without the greyed look. */}
      {onAsk
        ? <button type="button" className={`journal-step is-${step.state}`} title={t("workJournal.ask")} onClick={() => onAsk(step, name)}>{content}</button>
        : <div className={`journal-step is-${step.state} is-static`}>{content}</div>}
      {images.length ? (
        <button type="button" className="journal-step-image" title={view} aria-label={view} onClick={() => onView({ name, images })}>
          {IMAGE_ICON}
          {images.length > 1 ? <span className="journal-step-image-count">{images.length}</span> : null}
        </button>
      ) : null}
    </li>
  );
}

/** One saved image, fetched with the token and shown from an object URL that is released on close. */
function JournalImage({ path, load, alt }: { path: string; load: LoadJournalImage; alt: string }) {
  const { t } = useLocale();
  const [state, setState] = useState<{ url: string } | "loading" | "failed">("loading");
  useEffect(() => {
    const controller = new AbortController();
    let url: string | null = null;
    load(path, controller.signal)
      .then((blob) => { url = URL.createObjectURL(blob); setState({ url }); })
      .catch(() => { if (!controller.signal.aborted) setState("failed"); });
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [path, load]);
  if (state === "loading") return <div className="journal-image is-loading"><Spinner size={18} /></div>;
  if (state === "failed") return <p className="journal-image is-failed">{t("workJournal.imageFailed")}</p>;
  return <a className="journal-image" href={state.url} target="_blank" rel="noreferrer"><img src={state.url} alt={alt} /></a>;
}

/** The images a step returned, in a dialog. Escape closes the dialog, not the panel behind it. */
function ImageViewer({ viewing, load, onClose }: { viewing: Viewing; load: LoadJournalImage; onClose: () => void }) {
  useEffect(() => {
    const keep = (event: KeyboardEvent) => { if (event.key === "Escape") event.preventDefault(); };
    document.addEventListener("keydown", keep);
    return () => document.removeEventListener("keydown", keep);
  }, []);
  return (
    <Modal open size="4xl" title={viewing.name} onClose={onClose}>
      <div className="journal-images">
        {viewing.images.map((path) => <JournalImage key={path} path={path} load={load} alt={viewing.name} />)}
      </div>
    </Modal>
  );
}

/** lucide `ChevronRight`, turned down when open. */
const CHEVRON = icon(<path d="m9 6 6 6-6 6" />);
/** lucide `Play`: the run was picked up here. */
const CONTINUED_ICON = icon(<path d="M6 4l14 8-14 8V4z" />);

/** The step's name as the user saw it: its label, or the tool's own. */
function stepName(step: JournalStep, t: (key: string) => string): string {
  const key = toolLabelKey(step.tool);
  return step.displayName || (key ? t(key) : humanizeToolName(step.tool));
}

/**
 * Where a cut-off run stopped, and the way on: under the last group, naming the
 * step the run died inside (its result never came back) or saying it stopped
 * between steps, with Continue — the same control as the view's header, placed
 * where the reader is looking for it.
 */
function StoppedHere({ groups, continuation }: { groups: readonly JournalGroup[]; continuation: ContinueState }) {
  const { t } = useLocale();
  if (!continuable(continuation.pending)) return null;
  const step = continuation.pending.kind === "tools" ? stoppedAtStep(groups) : null;
  return (
    <li className="journal-stopped" data-step={step?.n}>
      <p className="journal-stopped-where">
        {step ? t("workflows.continue.stoppedAt", { step: stepName(step, t) }) : t("workflows.continue.stoppedThinking")}
      </p>
      <ContinueBar state={continuation} />
    </li>
  );
}

/** How many steps, and the ones that did not go well: what a folded row must still say. */
function StepSummary({ steps }: { steps: readonly JournalStep[] }) {
  const { t } = useLocale();
  const counts = stepCounts(steps);
  const parts = [
    ...(counts.failed ? [t("workJournal.failedCount", { count: counts.failed })] : []),
    ...(counts.unconfirmed ? [t("workJournal.unconfirmedCount", { count: counts.unconfirmed })] : []),
  ];
  return parts.length ? <span className="journal-fold-note">{parts.join(" · ")}</span> : null;
}

/**
 * A run of steps: shown as is when short, otherwise one row that opens. The row
 * keeps the count and anything that went wrong, so folding never hides trouble.
 */
function StepBlock({ block, open, onToggle, onAsk, onView }: {
  block: JournalStepBlock;
  open: boolean;
  onToggle: () => void;
  onAsk: ((step: JournalStep, name: string) => void) | null;
  onView: (viewing: Viewing) => void;
}) {
  const { t } = useLocale();
  const steps = block.steps.map((step) => <Step key={step.n} step={step} onAsk={onAsk} onView={onView} />);
  if (block.steps.length <= JOURNAL_LOOSE_STEPS) return <>{steps}</>;
  const label = t("workJournal.stepCount", { count: block.steps.length });
  return (
    <li className={`journal-block${open ? " is-open" : ""}`}>
      <button type="button" className="journal-fold-row" aria-expanded={open} onClick={onToggle}>
        <span className="journal-fold-chevron">{CHEVRON}</span>
        <span className="journal-fold-label">{label}</span>
        <StepSummary steps={block.steps} />
      </button>
      {open ? <ol className="journal-items">{steps}</ol> : null}
    </li>
  );
}

/**
 * The Work Journal in the side panel: each user message, then what was done
 * and said after it, oldest first and scrolled to the newest.
 *
 * Folded in two levels so a hundred steps stay readable. Older messages fold
 * to one line with their counts; the latest is open. Inside it, the agent's
 * own messages divide the steps (a run with none is sliced every ten). The
 * last run stays open, and so does any run with a step whose result is not
 * confirmed: that is where an interrupted run stopped. What the reader opens
 * or closes is not remembered: every opening starts from these defaults.
 */
export function JournalPanel({ groups, live, continuation, onAsk, loadImage }: {
  groups: JournalGroup[];
  live: boolean;
  /** Whether the last run can be continued, and the click that does it; the panel marks where it stopped. */
  continuation: ContinueState;
  onAsk: ((step: JournalStep, name: string) => void) | null;
  loadImage: LoadJournalImage;
}) {
  const { t } = useLocale();
  const body = useRef<HTMLDivElement>(null);
  const seen = useRef(0);
  const count = groups.reduce((sum, group) => sum + group.items.length + 1, 0);
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const isOpen = (key: string, fallback: boolean) => opened[key] ?? fallback;
  const toggle = (key: string, now: boolean) => setOpened((prev) => ({ ...prev, [key]: !now }));
  const [viewing, setViewing] = useState<Viewing | null>(null);

  // Follow new entries only while the reader is already at the bottom.
  useLayoutEffect(() => {
    const element = body.current;
    if (!element) return;
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
    if (seen.current === 0 || atBottom) element.scrollTop = element.scrollHeight;
    seen.current = count;
  }, [count]);

  return (
    // No title row and no close: it is a tab of the workflow's view, which names it.
    <aside className="work-settings-panel journal-panel" aria-label={t("workJournal.title")}>
      <div className="work-settings-content">
        <div ref={body} className="work-settings-body journal-body">
          {groups.length === 0 ? <p className="journal-none">{t("workJournal.empty")}</p> : groups.map((group, index) => {
            const latest = index === groups.length - 1;
            const groupOpen = isOpen(group.key, latest);
            const blocks = toBlocks(group.items);
            const lastSteps = [...blocks].reverse().find((block) => block.kind === "steps");
            const allSteps = group.items.filter((item): item is JournalStep => item.kind === "step");
            return (
              <section key={group.key} className={`journal-group${groupOpen ? " is-open" : ""}`}>
                <button type="button" className="journal-fold-row journal-user" aria-expanded={groupOpen} title={group.user?.text} onClick={() => toggle(group.key, groupOpen)}>
                  <span className="journal-fold-chevron">{CHEVRON}</span>
                  <span className="journal-user-text">{group.user?.text ?? t("workJournal.start")}</span>
                  <span className="journal-fold-label">{t("workJournal.stepCount", { count: allSteps.length })}</span>
                  <StepSummary steps={allSteps} />
                </button>
                {groupOpen ? (
                  <ol className="journal-items">
                    {blocks.map((block) => {
                      if (block.kind === "said") {
                        return <li key={block.said.n}><FoldedLine className="journal-said"><Markdown text={block.said.text} /></FoldedLine></li>;
                      }
                      if (block.kind === "continued") {
                        return (
                          <li key={`c${block.mark.n}`} className="journal-continued" title={new Date(block.mark.at).toLocaleString()}>
                            <span className="journal-step-icon">{CONTINUED_ICON}</span>
                            <span>{t("workJournal.continued")}</span>
                          </li>
                        );
                      }
                      const counts = stepCounts(block.steps);
                      const fallback = (latest && block === lastSteps) || counts.unconfirmed > 0 || (live && counts.running > 0);
                      const open = isOpen(block.key, fallback);
                      return <StepBlock key={block.key} block={block} open={open} onToggle={() => toggle(block.key, open)} onAsk={onAsk} onView={setViewing} />;
                    })}
                    {latest && !live ? <StoppedHere groups={groups} continuation={continuation} /> : null}
                  </ol>
                ) : null}
              </section>
            );
          })}
        </div>
      </div>
      {viewing ? <ImageViewer viewing={viewing} load={loadImage} onClose={() => setViewing(null)} /> : null}
    </aside>
  );
}
